import { UiComponent } from '../../../types/ui-component';
import { classNames } from '@cotera/client/app/components/utils';
import { forwardRef } from 'react';

const SIZE_CLASS = {
  heading: 'text-4xl leading-10',
  title: 'text-xl  leading-6',
  label: 'text-sm leading-6',
  section: 'text-base',
} as const;

const SUB_SIZE_CLASS = {
  heading: 'text-xl opacity-70 ',
  title: 'text-sm opacity-70 mt-1',
  label: 'text-sm opacity-70 ',
  section: 'text-sm opacity-70 ',
} as const;

const PRE_SIZE_CLASS = {
  heading: 'text-base',
  title: 'text-xs ml-0.5 mb-1 opacity-70',
  label: 'text-xs',
  section: 'text-xs',
} as const;

type Props = {
  title?: string;
  children?: React.ReactNode;
  pre?: string;
  subtitle?: string;
  /**
   * Extra classes for the subtitle line alone.
   *
   * So a caller can hide or resize the subtitle at a breakpoint without
   * restating the styling of it — a copy that looks right the day it is written
   * and drifts the first time this component's own subtitle changes.
   */
  subtitleClassName?: string;
  /**
   * Something pinned to the title *line* — a favourite star, a status chip.
   *
   * It belongs here rather than beside the whole component because this is a
   * two-line stack whenever {@link Props.subtitle} is set, and a caller can
   * only reach the stack, not the line. Centring a mark against the stack from
   * outside lands it between the name and the subtitle, adorning neither.
   *
   * Rendered outside the title's own span, so a `truncate` on the title still
   * eats the name rather than the mark.
   */
  adornment?: React.ReactNode;
  type?: 'heading' | 'title' | 'label' | 'section';
  className?: string;
  onClick?: () => void;
};

export const Title = forwardRef<HTMLDivElement, UiComponent<Props>>(
  (
    {
      className,
      pre,
      subtitle: sub,
      subtitleClassName,
      adornment,
      title,
      children,
      type = 'label',
      onClick,
    },
    ref
  ) => {
    const titleSpan = (
      <span
        className={classNames(
          'tracking-tight font-medium text-foreground',
          SIZE_CLASS[type],
          className
        )}
      >
        {title ?? children}
      </span>
    );

    return (
      <div
        onClick={onClick}
        className={classNames('flex flex-col font-title', className)}
        ref={ref}
      >
        {pre && (
          <span
            className={classNames(
              'mb-0.5',
              PRE_SIZE_CLASS[type],
              'text-muted-foreground'
            )}
          >
            {pre}
          </span>
        )}
        {/* Only wrapped when there is something to pin, so every existing
            caller renders exactly the markup it did before. `min-w-0` is what
            lets the title keep truncating now that it sits a level deeper. */}
        {adornment === undefined || adornment === null ? (
          titleSpan
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            {titleSpan}
            <span className="flex shrink-0 items-center">{adornment}</span>
          </div>
        )}
        {sub && (
          <span
            className={classNames(
              SUB_SIZE_CLASS[type],
              'text-muted-foreground/60 font-light italic',
              subtitleClassName
            )}
          >
            {sub}
          </span>
        )}
      </div>
    );
  }
);

export const Label = forwardRef<HTMLDivElement, UiComponent<Props>>(
  (
    {
      className,
      pre,
      subtitle: sub,
      subtitleClassName,
      title,
      children,
      type = 'label',
      onClick,
    },
    ref
  ) => {
    return (
      <div
        onClick={onClick}
        className={classNames(
          'flex flex-col font-title font-medium',
          className
        )}
        ref={ref}
      >
        {pre && (
          <span
            className={classNames(
              'mb-0.5',
              PRE_SIZE_CLASS[type],
              'text-muted-foreground'
            )}
          >
            {pre}
          </span>
        )}
        <span
          className={classNames(
            'tracking-tight font-medium text-foreground',
            SIZE_CLASS[type],
            className
          )}
        >
          {title ?? children}
        </span>
        {sub && (
          <span
            className={classNames(
              SUB_SIZE_CLASS[type],
              'text-foreground',
              subtitleClassName
            )}
          >
            {sub}
          </span>
        )}
      </div>
    );
  }
);
